import LogoSmallIcon from '../images/LogoSmall.svg?react';
import { Box } from '@/components/Box/Box';
import { OpcoUserStatus } from '../../ui/OpcoUserStatus/OpcoUserStatus';
import { Button } from '@/components/Button/Button';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { useState } from 'react';
import { isBlockedOpcoUserStatus } from '../../helpers/opcoUserHelpers';
import { formatPhone } from '@/helpers/formatHelpers';
import { OpcoUserType } from '@/bundle/_Opco/types/types';
import { useMutation } from '@tanstack/react-query';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import { PIN_TOKEN_EVENT_SCOPE } from '@/const/shared';
import { ResetTimeInfo } from './ResetTimeInfo/ResetTimeInfo';
import {
  UserDetailsActionRow,
  UserDetailsCard,
  UserDetailsRow,
  UserDetailsRowTitle,
  UserDetailsRowValue,
  UserDetailsWrapper,
} from '@/bundle/_Opco/shared/styles';
import { UserRole } from '@/bundle/_Opco/shared/UserRole/UserRole';
import { UserDetailsHeader } from '@/bundle/shared/components/UserDetails/UserDetailsHeader/UserDetailsHeader';
import { CheckPinCodeModal } from '@/bundle/shared/components/CheckPinCodeModal/CheckPinCodeModal';
import { ToggleBlockUserConfirmModal } from '@/bundle/shared/components/modals/ToggleBlockUserConfirmModal';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { blockOpcoUser, resetOpcoUser } from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/api/api';

export type OpcoUserAccountDetailsType = {
  userDetails: OpcoUserType;
  onSuccess: () => void;
  isOwnAccount: boolean;
};

export const OpcoUserAccountDetails = ({ userDetails, onSuccess, isOwnAccount }: OpcoUserAccountDetailsType) => {
  const { user, role } = userDetails;
  const { email, mobile_phone, status } = user;

  const [actionEventScope, setActionEventScope] = useState(null);
  const [isToggleBlockUserModalOpen, setIsToggleBlockUserModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isPinCodeOpen, setIsPinCodeOpen] = useState(false);

  const lastResetDetails = user?.last_reset_details;

  const { mutate: blockOpcoUserMutate } = useMutation({
    mutationKey: ['block_opco_user'],
    mutationFn: (pinToken: string) => {
      return blockOpcoUser(user?.id, pinToken);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      showSnackbar('User account has been blocked.');
      onSuccess();
    },
    onSettled() {
      setIsToggleBlockUserModalOpen(false);
    },
  });

  const { mutate: resetOpcoUserMutate, isPending: isPendingResetOpcoUser } = useMutation({
    mutationKey: ['reset_opco_user'],
    mutationFn: (pinToken: string) => {
      return resetOpcoUser(user?.id, pinToken);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      showSnackbar('User account has been reset.');
      onSuccess();
    },
    onSettled() {
      setIsToggleBlockUserModalOpen(false);
    },
  });

  const closePinCode = () => {
    setIsPinCodeOpen(false);
    setActionEventScope(null);
  };

  const checkPinCodeSuccess = (pinToken: string) => {
    if (!actionEventScope) return;

    if (actionEventScope === PIN_TOKEN_EVENT_SCOPE.BLOCK_OPCO_USER) {
      blockOpcoUserMutate(pinToken);
      closePinCode();

      return;
    }

    if (actionEventScope === PIN_TOKEN_EVENT_SCOPE.RESET_OPCO_USER) {
      resetOpcoUserMutate(pinToken);
      closePinCode();

      return;
    }
  };

  const openBlockOpcoUserPinCode = () => {
    setIsPinCodeOpen(true);
    setIsToggleBlockUserModalOpen(false);
    setActionEventScope(PIN_TOKEN_EVENT_SCOPE.BLOCK_OPCO_USER);
  };

  const openResetOpcoUserPinCode = () => {
    setIsPinCodeOpen(true);
    setIsResetModalOpen(false);
    setActionEventScope(PIN_TOKEN_EVENT_SCOPE.RESET_OPCO_USER);
  };

  const isBlockedUserStatus = isBlockedOpcoUserStatus(status);
  const formattedMobilePhone = formatPhone(mobile_phone);

  return (
    <UserDetailsWrapper>
      <UserDetailsHeader
        title='WireVault Account Information'
        icon={<LogoSmallIcon />}
        isOwnAccount={isOwnAccount}
        subTitle='You must enter your PIN code to edit these fields.'
      />
      <UserDetailsCard>
        <UserDetailsRow>
          <UserDetailsRowTitle>Email Address</UserDetailsRowTitle>
          <UserDetailsRowValue>{email}</UserDetailsRowValue>
        </UserDetailsRow>
        <UserDetailsRow>
          <UserDetailsRowTitle>Mobile Phone Number</UserDetailsRowTitle>
          <UserDetailsRowValue>{formattedMobilePhone}</UserDetailsRowValue>
        </UserDetailsRow>
        <UserDetailsRow>
          <UserDetailsRowTitle>Role</UserDetailsRowTitle>
          <UserRole role={role} />
        </UserDetailsRow>
        <UserDetailsActionRow>
          <Box>
            <UserDetailsRowTitle>Status</UserDetailsRowTitle>
            <OpcoUserStatus status={status} />
          </Box>
          {!isOwnAccount && (
            <Box display='flex' columnGap='12px' alignItems='center'>
              {!isBlockedUserStatus && (
                <Button
                  width={71}
                  size='medium'
                  variant='secondary'
                  onClick={() => setIsToggleBlockUserModalOpen(true)}
                >
                  Block
                </Button>
              )}
              <Button width={71} size='medium' variant='secondary' onClick={() => setIsResetModalOpen(true)}>
                Reset
              </Button>
            </Box>
          )}
        </UserDetailsActionRow>
        {!!lastResetDetails && <ResetTimeInfo info={lastResetDetails} />}
      </UserDetailsCard>

      <ToggleBlockUserConfirmModal
        isOpen={isToggleBlockUserModalOpen}
        isBlockedUser={false}
        onClose={() => setIsToggleBlockUserModalOpen(false)}
        onConfirm={openBlockOpcoUserPinCode}
      />

      <ConfirmModal
        isOpen={isResetModalOpen}
        header='Reset Account'
        body={
          <>
            The User will need to complete the onboarding procedure once again.
            <br />
            An account can be reset once per hour.
          </>
        }
        onClose={() => setIsResetModalOpen(false)}
        onConfirm={openResetOpcoUserPinCode}
        isLoading={isPendingResetOpcoUser}
      />
      {isPinCodeOpen && (
        <CheckPinCodeModal
          isOpen={isPinCodeOpen}
          eventScope={actionEventScope}
          onSuccess={checkPinCodeSuccess}
          onClose={() => setIsPinCodeOpen(false)}
        />
      )}
    </UserDetailsWrapper>
  );
};
